import { useMemo, useState } from 'react';
import type { TeamMemberInstallmentCellDto } from '../../../../domain/contracts/api.contracts';
import {
  buildGridSlots,
  getInstallmentStatusSpec,
  type GridPhase,
  type GridSlot,
} from '../../../../domain/calculators/installments';
import { formatDate } from '../../../../domain/formatters/date';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { AmountDisplay } from '../../../components/ui/AmountDisplay';
import { Badge } from '../../../components/ui/Badge';

const LEGEND: GridPhase[] = ['cleared', 'advance', 'due', 'delinquent', 'upcoming'];

/**
 * Compact 10 × 12 record (one row per tenure year) using the unified status table.
 * Binds slots by installmentNumber (1..120), never array index (DEVELOPMENT_PLAN.md §6.3 / §3.1).
 */
export function InstallmentMiniGrid({ cells }: { cells: TeamMemberInstallmentCellDto[] }) {
  const { t, locale } = useTranslation();
  const [selected, setSelected] = useState<number | null>(null);

  const slots = useMemo(
    () => buildGridSlots(cells as unknown as TeamMemberInstallmentCellDto[]),
    [cells],
  );

  const rows = useMemo(() => {
    const years: GridSlot[][] = [];
    for (let y = 0; y < 10; y++) {
      years.push(slots.slice(y * 12, y * 12 + 12));
    }
    return years;
  }, [slots]);

  const active = selected !== null ? slots.find((s) => s.installmentNumber === selected) : undefined;
  const activeRow = active?.row as (TeamMemberInstallmentCellDto | null | undefined);

  return (
    <div className="m360-grid-wrap">
      <div className="m360-grid" role="grid" aria-label={t('team.gridTitle')} data-testid="member360-grid">
        {rows.map((year, y) => (
          <div key={y} className="m360-grid-row" role="row">
            <span className="m360-grid-year" role="rowheader">{t('team.yearShort', { n: y + 1 })}</span>
            {year.map((slot) => {
              const row = slot.row as (TeamMemberInstallmentCellDto | null | undefined);
              const statusSpec = getInstallmentStatusSpec(row?.status, row?.dueDate);
              const label = row
                ? `${t('installments.month')} ${slot.installmentNumber}, ${t(statusSpec.labelKey)}${
                    row.paidDate ? `, ${formatDate(row.paidDate, locale)}` : ''
                  }${row.channel ? `, ${t(`team.ch_${row.channel}`)}` : ''}`
                : `${t('installments.month')} ${slot.installmentNumber}, ${t('installments.not_generated')}`;

              return (
                <button
                  key={slot.installmentNumber}
                  type="button"
                  role="gridcell"
                  className={`m360-cell m360-cell--${slot.phase}${selected === slot.installmentNumber ? ' is-selected' : ''}`}
                  aria-label={label}
                  title={label}
                  aria-pressed={selected === slot.installmentNumber}
                  onClick={() => setSelected(selected === slot.installmentNumber ? null : slot.installmentNumber)}
                  data-testid="member360-cell"
                  data-phase={slot.phase}
                >
                  {statusSpec.glyph}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <ul className="m360-legend" aria-hidden>
        {LEGEND.map((p) => (
          <li key={p}>
            <span className={`m360-cell m360-cell--${p} m360-cell--swatch`} /> {t(`installments.phase_${p}`)}
          </li>
        ))}
      </ul>

      {active && activeRow ? (
        <div className="m360-cell-detail" data-testid="member360-cell-detail">
          <strong>
            {t('installments.month')} {active.installmentNumber}
          </strong>
          {(() => {
            const spec = getInstallmentStatusSpec(activeRow.status, activeRow.dueDate);
            return <Badge tone={spec.tone}>{t(spec.labelKey)}</Badge>;
          })()}
          <span>
            {t('team.dueOn')}: {formatDate(activeRow.dueDate, locale)}
          </span>
          <span>
            {t('installments.amount')}: <AmountDisplay amount={activeRow.amount} />
          </span>
          {activeRow.paidDate && (
            <span>
              {t('team.paidOn')}: {formatDate(activeRow.paidDate, locale)}
            </span>
          )}
          {activeRow.channel && (
            <span>
              {t('team.channel')}: {t(`team.ch_${activeRow.channel}`)}
            </span>
          )}
        </div>
      ) : active ? (
        <div className="m360-cell-detail" data-testid="member360-cell-detail">
          <strong>
            {t('installments.month')} {active.installmentNumber}
          </strong>
          <Badge tone="neutral">{t('installments.not_generated')}</Badge>
        </div>
      ) : (
        <p className="m360-hint">{t('team.gridHint')}</p>
      )}
    </div>
  );
}
